import { useState } from 'react';
import { Box, Button, CircularProgress, Step, StepLabel, Stepper, Typography } from '@mui/material';
import ContentPasteIcon from '@mui/icons-material/ContentPaste';
import { css } from '@emotion/react';

import { GeopostPopup } from 'components/geopostPopup/GeopostPopup';
import { useCreateUploadQueueItems } from '../api/useQueueItemController';
import { useGetUploadInformation } from '../api/useQueueGroupController';
import { IWizardFile } from '../models/interfaces/IWizardFile';
import { QueueType } from '../models/enums/QueueType';
import { UploadType } from '../models/enums/UploadType';
import { getCopiedParamValues } from '../utils/copiedParamValues';
import { getQueueTypeHandler } from '../queueType/QueueTypeRegistry';
import { resolveAvailableUploadTypes } from '../uploadType/UploadTypeRegistry';
import { QueueProcessingConfirmFiles } from './QueueProcessingConfirmFiles';
import { QueueProcessingParams } from './QueueProcessingParams';
import { QueueProcessingUpload } from './QueueProcessingUpload';

const WIZARD_STEPS = ['Select Files', 'Confirm Files', 'Parameters'];

const wizardStyle = css({
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minHeight: 0,
    width: '100%',
    overflow: 'hidden',
    '& .wizard-stepper': {
        flexShrink: 0,
        padding: '16px 24px',
        borderBottom: '1px solid #e0e0e0',
        backgroundColor: '#fafafa',
    },
    '& .wizard-content': {
        flex: 1,
        minHeight: 0,
        overflow: 'auto',
    },
    '& .wizard-loading': {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100%',
    },
    '& .wizard-footer': {
        flexShrink: 0,
        display: 'flex',
        justifyContent: 'flex-end',
        gap: 8,
        padding: '12px 24px',
        borderTop: '1px solid #e0e0e0',
        backgroundColor: '#f8f9fa',
    },
});

interface IQueueProcessingAddFilesWizardProps {
    open: boolean;
    onClose: () => void;
    groupToken: string;
    queueType: QueueType;
}

export function QueueProcessingAddFilesWizard({ open, onClose, groupToken, queueType }: IQueueProcessingAddFilesWizardProps) {
    const [activeStep, setActiveStep] = useState(0);
    const [wizardFiles, setWizardFiles] = useState<IWizardFile[]>([]);
    const [confirmedFiles, setConfirmedFiles] = useState<IWizardFile[]>([]);
    const [params, setParams] = useState<Record<string, unknown>>({});
    const [paramsInitialValues, setParamsInitialValues] = useState<Record<string, string> | undefined>(undefined);
    const [paramsKey, setParamsKey] = useState(0);
    const [activeUploadType, setActiveUploadType] = useState<UploadType | null>(null);
    const { data: uploadInfo, isLoading: isLoadingUploadInfo } = useGetUploadInformation();
    const { mutateAsync: createUploadQueueItems, isLoading: isProcessing } = useCreateUploadQueueItems();
    const availableTypes = uploadInfo ? resolveAvailableUploadTypes(uploadInfo) : [];

    function handleClose() {
        setActiveStep(0);
        setWizardFiles([]);
        setConfirmedFiles([]);
        setParams({});
        setActiveUploadType(null);
        onClose();
    }

    function handleNext() {
        setActiveStep((s) => s + 1);
    }

    function handleBack() {
        setActiveStep((s) => s - 1);
    }

    async function handleProcess() {
        const itemParams = Object.entries(params).map(([key, value]) => ({
            Param: key,
            Value: String(value),
        }));

        const handler = getQueueTypeHandler(queueType);
        const filesToProcess = confirmedFiles.length > 0 ? confirmedFiles : wizardFiles;

        await createUploadQueueItems({
            GroupToken: groupToken,
            UploadType: activeUploadType ?? UploadType.LocalUpload,
            Items: handler.buildItems(filesToProcess, itemParams),
        });

        window.location.reload();
    }

    const isLastStep = activeStep === WIZARD_STEPS.length - 1;
    const handler = getQueueTypeHandler(queueType);
    const canAdvance = (activeStep !== 0 || wizardFiles.length > 0) && (activeStep !== 1 || handler.groupFiles(wizardFiles).length > 0);

    return (
        <GeopostPopup
            open={open}
            onChangeOpen={handleClose}
            title='Add Files'
            width={960}
            height={660}
        >
            <Box css={wizardStyle}>
                <Box className='wizard-stepper'>
                    <Stepper activeStep={activeStep}>
                        {WIZARD_STEPS.map((label) => (
                            <Step key={label}>
                                <StepLabel>{label}</StepLabel>
                            </Step>
                        ))}
                    </Stepper>
                </Box>

                <Box className='wizard-content'>
                    <Box sx={{ display: activeStep === 0 ? 'initial' : 'none' }}>
                        {isLoadingUploadInfo ? (
                            <Box className='wizard-loading'>
                                <CircularProgress />
                            </Box>
                        ) : availableTypes.length === 0 ? (
                            <Box className='wizard-loading'>
                                <Typography variant='body2' color='text.secondary'>
                                    No upload methods are available for this project.
                                </Typography>
                            </Box>
                        ) : (
                            <QueueProcessingUpload
                                availableTypes={availableTypes}
                                queueType={queueType}
                                onFilesChange={setWizardFiles}
                                onUploadTypeChange={setActiveUploadType}
                            />
                        )}
                    </Box>
                    <Box sx={{ display: activeStep === 1 ? 'initial' : 'none' }}>
                        <QueueProcessingConfirmFiles files={wizardFiles} queueType={queueType} onConfirmedFilesChange={setConfirmedFiles} />
                    </Box>
                    <Box sx={{ display: activeStep === 2 ? 'flex' : 'none', flexDirection: 'column', height: '100%' }}>
                        {getCopiedParamValues() && (
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', px: 3, pt: 2 }}>
                                <Button
                                    size='small'
                                    variant='outlined'
                                    startIcon={<ContentPasteIcon sx={{ fontSize: 14 }} />}
                                    onClick={() => {
                                        const copied = getCopiedParamValues();
                                        if (copied) {
                                            setParams(copied);
                                            setParamsInitialValues(copied);
                                            setParamsKey((k) => k + 1);
                                        }
                                    }}
                                    sx={{ textTransform: 'none', fontSize: 12, borderColor: '#c5cae9', color: '#3949ab' }}
                                >
                                    Paste values
                                </Button>
                            </Box>
                        )}
                        <QueueProcessingParams key={paramsKey} queueType={queueType} onParamsChange={setParams} initialValues={paramsInitialValues} />
                    </Box>
                </Box>

                <Box className='wizard-footer'>
                    <Button variant='outlined' color='inherit' onClick={handleClose}>
                        Cancel
                    </Button>
                    {activeStep > 0 && (
                        <Button variant='outlined' onClick={handleBack}>
                            Back
                        </Button>
                    )}
                    <Button
                        variant='contained'
                        disableElevation
                        disabled={!canAdvance || isProcessing}
                        startIcon={isProcessing && isLastStep ? <CircularProgress size={16} color='inherit' /> : undefined}
                        onClick={isLastStep ? handleProcess : handleNext}
                    >
                        {isLastStep ? 'Process' : 'Next'}
                    </Button>
                </Box>
            </Box>
        </GeopostPopup>
    );
}
